import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Crosshair, Shield, Rocket, Radio, Users, Ship, Anchor } from 'lucide-react';
import { Ticker } from '../components/Ticker';
import { useBackgrounds } from '../context/BackgroundContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_9645c0b6-481f-4d07-81fb-c2ea43b51b14/artifacts/rw309x94_VoidWolves2-Photoroom.png";

export const Bridge = () => {
    const { getBackground } = useBackgrounds();
    const [stats, setStats] = useState({
        active_pilots: 126,
        fleet_vessels: 47,
        capital_ships: 12,
        active_operations: 3
    });
    const [activeOp, setActiveOp] = useState(null);
    
    const heroBg = getBackground('bridge');
    const strikeImg = getBackground('mission_strike');
    const escortImg = getBackground('mission_escort');
    const reconImg = getBackground('mission_recon');

    const missionTypes = [
        {
            icon: Crosshair,
            title: 'STRIKE',
            description: 'Rapid-deployment carrier operations. We bring the hangar to the fight.',
            stat: '23 MISSIONS',
            image: strikeImg
        },
        {
            icon: Shield,
            title: 'ESCORT',
            description: 'Multi-ship convoy protection through hostile jump points and interdiction zones.',
            stat: '47 CONVOYS',
            image: escortImg
        },
        {
            icon: Radio,
            title: 'RECON',
            description: 'Deep-space pathfinding and intelligence gathering ahead of the main fleet.',
            stat: '89 SECTORS',
            image: reconImg
        }
    ];

    useEffect(() => {
        fetchStats();
        fetchActiveOp();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/stats`);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchActiveOp = async () => {
        try {
            const response = await axios.get(`${API}/operations/active`);
            if (response.data.length > 0) {
                setActiveOp(response.data[0]);
            }
        } catch (error) {
            console.error('Failed to fetch active operations:', error);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505]" data-testid="bridge-page">
            {/* Hero Section */}
            <section className="hero-section" data-testid="hero-section">
                <div 
                    className="hero-bg"
                    style={{ backgroundImage: `url(${heroBg})` }}
                />
                <div className="hero-overlay" />
                
                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 pt-20">
                    <div className="mb-6">
                        <span className="font-mono text-xs tracking-[0.3em] text-red-500 uppercase">
                            Tactical Command Interface v4.126
                        </span>
                    </div>
                    
                    <img 
                        src={LOGO_URL} 
                        alt="Voidwolves" 
                        className="w-48 sm:w-64 mx-auto mb-8 hud-glow"
                        data-testid="hero-logo"
                    />
                    
                    <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter uppercase mb-2">
                        VOIDWOLVES
                    </h1>
                    <h2 className="font-mono text-lg sm:text-xl text-gray-400 tracking-[0.2em] mb-6">
                        126th Carrier Strike Group
                    </h2>
                    
                    <p className="font-body text-gray-400 text-lg italic mb-8 max-w-xl mx-auto">
                        "The void provides. The pack endures."
                    </p>
                    
                    <p className="font-mono text-xs text-gray-500 tracking-wider mb-12">
                        Operating across Stanton and beyond.
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
                        <Link 
                            to="/war-room" 
                            className="btn-primary px-8 py-4 text-sm"
                            data-testid="enlist-now-button"
                        >
                            Enlist Now
                        </Link>
                        <Link 
                            to="/hangar" 
                            className="btn-secondary px-8 py-4 text-sm"
                            data-testid="view-fleet-button"
                        >
                            View Our Fleet
                        </Link>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12" data-testid="stats-grid">
                        <div className="stat-card">
                            <div className="stat-number" data-testid="stat-pilots">{stats.active_pilots}</div>
                            <div className="stat-label">Active Pilots</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-number" data-testid="stat-vessels">{stats.fleet_vessels}</div>
                            <div className="stat-label">Fleet Vessels</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-number" data-testid="stat-capitals">{stats.capital_ships}</div>
                            <div className="stat-label">Capital Ships</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-number text-red-500" data-testid="stat-operations">{stats.active_operations}</div>
                            <div className="stat-label">Active Ops</div>
                        </div>
                    </div>

                    {/* Scroll indicator */}
                    <div className="font-mono text-xs text-gray-600 tracking-[0.3em] animate-bounce">
                        SCROLL
                    </div>
                </div>
            </section>

            {/* Ticker */}
            <Ticker />

            {/* Active Operation Banner */}
            {activeOp && (
                <section className="py-8 px-4" data-testid="active-operation-banner">
                    <div className="max-w-5xl mx-auto">
                        <div className="tactical-panel p-6">
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <div className="w-3 h-3 rounded-full status-active animate-pulse" />
                                    <div>
                                        <span className="font-mono text-xs text-gray-500 tracking-wider">ACTIVE OPERATION</span>
                                        <h3 className="font-heading font-bold text-xl text-white uppercase">
                                            {activeOp.code_name}
                                        </h3>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-6">
                                    <div>
                                        <span className="font-mono text-[10px] text-gray-500 tracking-wider block">STATUS</span>
                                        <span className="font-mono text-sm text-green-500 uppercase">{activeOp.status}</span>
                                    </div>
                                    <div>
                                        <span className="font-mono text-[10px] text-gray-500 tracking-wider block">SYSTEM</span>
                                        <span className="font-mono text-sm text-white">{activeOp.system}</span>
                                    </div>
                                    <div>
                                        <span className="font-mono text-[10px] text-gray-500 tracking-wider block">OBJECTIVE</span>
                                        <span className="font-mono text-sm text-gray-300">{activeOp.objective}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Mission Types */}
            <section className="py-24 px-4" data-testid="mission-types-section">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="font-mono text-xs text-red-500 tracking-[0.3em]">TACTICAL CAPABILITIES</span>
                        <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white uppercase mt-2">
                            Mission Profile
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {missionTypes.map((mission, index) => (
                            <div key={index} className="mission-card" data-testid={`mission-card-${mission.title.toLowerCase()}`}>
                                {mission.image && (
                                    <div className="mb-4 -mx-8 -mt-8">
                                        <img 
                                            src={mission.image} 
                                            alt={mission.title}
                                            className="w-full h-40 object-cover"
                                        />
                                    </div>
                                )}
                                <div className="mission-icon">
                                    <mission.icon className="w-6 h-6 text-red-500" />
                                </div>
                                <h3 className="font-heading font-bold text-xl text-white uppercase mb-2">
                                    {mission.title}
                                </h3>
                                <p className="font-body text-gray-400 text-sm mb-4">
                                    {mission.description}
                                </p>
                                <span className="font-mono text-xs text-red-500 tracking-wider">
                                    {mission.stat}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="py-24 px-4 bg-gradient-to-t from-black to-transparent">
                <div className="max-w-4xl mx-auto text-center">
                    <Anchor className="w-12 h-12 text-red-500 mx-auto mb-6" />
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl text-white uppercase mb-4">
                        Join The Pack
                    </h2>
                    <p className="font-body text-gray-400 mb-8 max-w-2xl mx-auto">
                        The 126th Carrier Strike Group is always looking for dedicated pilots, 
                        logistics specialists, and marine operators to join our ranks.
                    </p>
                    <Link 
                        to="/war-room" 
                        className="btn-primary px-10 py-4 text-sm inline-block"
                        data-testid="join-cta-button"
                    >
                        Begin Enlistment
                    </Link>
                </div>
            </section>
        </div>
    );
};
